import React from "react";
import {
  Box, Card, CardContent, Typography, Avatar, Grid,
  Divider, Chip, Stack
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import BadgeIcon from "@mui/icons-material/Badge";
import EmailIcon from "@mui/icons-material/Email";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../components/PageHeader.js";
import type { AuthClaims } from "@kappa-v2/contracts";

interface UserPageProps {
  user: AuthClaims | null;
}

export function UserPage({ user }: UserPageProps) {
  const { t } = useTranslation();
  const initials = user?.name
    ? user.name.split(" ").map((p: string) => p[0]).join("").toUpperCase().slice(0, 2)
    : (user?.preferred_username ?? "?").slice(0, 2).toUpperCase();

  const roles = user?.realm_access?.roles?.filter((r: string) =>
    !["offline_access", "uma_authorization", "default-roles-kappa"].includes(r)
  ) ?? [];

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <PageHeader title={t("user.title")} subtitle={t("user.subtitle")} />
      <Box sx={{ p: 3, flex: 1 }}>
        <Grid container spacing={3} sx={{ maxWidth: 800 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%" }}>
              <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, gap: 2 }}>
                <Avatar sx={{ width: 80, height: 80, bgcolor: "primary.main", fontSize: "2rem" }}>
                  {user ? initials : <AccountCircleIcon sx={{ fontSize: 40 }} />}
                </Avatar>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h6" fontWeight={700}>
                    {user?.name ?? user?.preferred_username ?? "—"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email ?? "—"}
                  </Typography>
                </Box>
                {roles.length > 0 && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="center">
                    {roles.map((r: string) => (
                      <Chip key={r} label={r} size="small" color="primary" variant="outlined"
                        sx={{ fontSize: "0.7rem", mb: 0.5 }} />
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                  {t("user.accountDetails")}
                </Typography>
                {[
                  { icon: <AccountCircleIcon fontSize="small" />, label: t("user.username"), value: user?.preferred_username ?? "—", mono: false },
                  { icon: <BadgeIcon fontSize="small" />, label: t("user.fullName"), value: user?.name ?? "—", mono: false },
                  { icon: <EmailIcon fontSize="small" />, label: t("user.email"), value: user?.email ?? "—", mono: false },
                  { icon: <VpnKeyIcon fontSize="small" />, label: t("user.userId"), value: user?.sub ?? "—", mono: true }
                ].map((item, idx) => (
                  <React.Fragment key={item.label}>
                    {idx > 0 && <Divider sx={{ my: 1.5 }} />}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ color: "text.disabled", display: "flex", flexShrink: 0 }}>
                        {item.icon}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}
                          sx={{ textTransform: "uppercase", letterSpacing: "0.04em", display: "block" }}>
                          {item.label}
                        </Typography>
                        <Typography variant="body2"
                          fontWeight={item.mono ? 400 : 600}
                          color={item.mono ? "text.secondary" : "text.primary"}
                          sx={{ wordBreak: "break-all" }}>
                          {item.value}
                        </Typography>
                      </Box>
                    </Box>
                  </React.Fragment>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
